import Component from "@glimmer/component";
import { cached } from "@glimmer/tracking";
import { action } from "@ember/object";
import { getOwner } from "@ember/owner";
import curryComponent from "ember-curry-component";
import { resolveFieldControl } from "discourse/form-kit/lib/field-control";
import ValidationParser from "discourse/form-kit/lib/validation-parser";
import Validator from "discourse/form-kit/lib/validator";
import dUniqueId from "discourse/ui-kit/helpers/d-unique-id";

/**
 * Represents a field in a form with validation, registration, and field data management capabilities.
 */
export default class FKFieldData extends Component {
  /**
   * Unique identifier for the field.
   * @type {string}
   */
  id = dUniqueId();

  /**
   * Unique identifier for the field's error element.
   * @type {string}
   */
  errorId = dUniqueId();

  #controlArgs = { field: this };

  // Set by legacy controls in their constructor (during render),
  // read by the applyControlType modifier (post-render)
  _legacyControlType;

  /**
   * Initializes the FKFieldData component.
   * Validates the presence of required arguments and registers the field.
   * @throws {Error} If `@title` is not provided.
   */
  constructor() {
    super(...arguments);

    if (!this.args.title?.toString?.()?.length) {
      throw new Error("@title is required on `<form.Field />`.");
    }
  }

  /**
   * Retrieves the current value of the field.
   * @type {any}
   */
  get value() {
    return this.args.data.get(this.name);
  }

  /**
   * Parses the validation rules for the field.
   * @type {Object|null}
   */
  get rules() {
    return this.args.validation
      ? ValidationParser.parse(this.args.validation)
      : null;
  }

  /**
   * Updates the value of the field and triggers revalidation.
   * @param {any} value - The new value for the field.
   * @returns {Promise<void>}
   */
  @action
  async set(value) {
    if (this.args.onSet) {
      await this.args.onSet(value, {
        set: this.args.set,
        name: this.name,
        parentName: this.args.parentName,
        index: this.args.collectionIndex,
      });
    } else {
      await this.args.set(this.name, value, {
        index: this.args.collectionIndex,
      });
    }

    this.args.triggerRevalidationFor(this.name);
  }

  /**
   * Title of the field.
   * @type {string}
   */
  get title() {
    return this.args.title;
  }

  get hasExplicitType() {
    return this.args.type !== undefined;
  }

  get type() {
    return this.args.type ?? this._legacyControlType;
  }

  set type(value) {
    this._legacyControlType = value;
  }

  /**
   * Contextual component for the control set by `@type`.
   * @type {Component}
   */
  @cached
  get Control() {
    return curryComponent(
      resolveFieldControl(this.type, getOwner(this)),
      this.#controlArgs,
      getOwner(this)
    );
  }

  /**
   * Format of the field.
   * @type {string}
   */
  get format() {
    return this.args.format;
  }

  /**
   * Format of the title and description (label area). Overrides @format
   * for the title and description, leaving the field's content area at
   * @format. Useful when long descriptions need more room than the input.
   * @type {string}
   */
  get labelFormat() {
    return this.args.labelFormat;
  }

  /**
   * Tooltip component of the field.
   * @type {string|Component}
   */
  get tooltip() {
    return this.args.tooltip;
  }

  /**
   * Indicates whether the field is disabled.
   * Defaults to `false`.
   * @type {boolean}
   */
  get disabled() {
    return this.args.disabled ?? false;
  }

  /**
   * Description of the field.
   * @type {string}
   */
  get description() {
    return this.args.description;
  }

  /**
   * Placeholder text for input fields.
   * @type {string}
   */
  get placeholder() {
    return this.args.placeholder;
  }

  /**
   * Help text of the field.
   * @type {string}
   */
  get helpText() {
    return this.args.helpText;
  }

  get error() {
    return (this.args.errors ?? {})[this.name];
  }

  get descriptionId() {
    return `${this.id}-description`;
  }

  get helpTextId() {
    return `${this.id}-help-text`;
  }

  get describedBy() {
    const ids = [];

    if (this.description) {
      ids.push(this.descriptionId);
    }

    if (this.helpText) {
      ids.push(this.helpTextId);
    }

    if (this.error) {
      ids.push(this.errorId);
    }

    return ids.length ? ids.join(" ") : undefined;
  }

  /**
   * Indicates whether to show the title rendered by the field container.
   * Has no effect on controls that render the title themselves; see
   * `showControlTitle`. Defaults to `true`.
   * @type {boolean}
   */
  get showTitle() {
    return this.args.showTitle ?? true;
  }

  /**
   * Indicates whether a control that renders the field's title itself, inline
   * in its own markup, should render it. Defaults to `true`.
   * @type {boolean}
   */
  get showControlTitle() {
    return this.args.showControlTitle ?? true;
  }

  /**
   * Indicates whether to show the `(optional)` marker next to the title when
   * the field has no `required` validation rule.
   * Defaults to `true`.
   * @type {boolean}
   */
  get showOptional() {
    return this.args.showOptional ?? true;
  }

  /**
   * Function to add errors to the field.
   * @type {Function}
   */
  get addError() {
    return this.args.addError;
  }

  /**
   * Constructs the unique name for the field.
   * @type {string}
   * @throws {Error} If `name` is not a string or contains invalid characters.
   */
  get name() {
    if (!this.args.name && this.args.parentName) {
      return this.args.parentName;
    }

    const name = this.args.name.toString();

    if (name?.includes(".") || name?.includes("-")) {
      throw new Error("@name can't include `.` or `-`.");
    }

    if (this.args.parentName) {
      return `${this.args.parentName}.${name}`;
    }

    return name;
  }

  get normalizedName() {
    return this.name.replace(/\./g, "-");
  }

  /**
   * Validation rules for the field.
   * @type {string|Object}
   */
  get validation() {
    return this.args.validation;
  }

  /**
   * Custom validation function.
   * @type {Function}
   */
  get customValidate() {
    return this.args.validate;
  }

  /**
   * Indicates if the field is required.
   * Derived from validation rules.
   * @type {boolean}
   * @readonly
   */
  get required() {
    return this.rules?.required ?? false;
  }

  /**
   * Maximum length of the field value.
   * Derived from validation rules.
   * @type {number|null}
   * @readonly
   */
  get maxLength() {
    return this.rules?.length?.max ?? null;
  }

  /**
   * Minimum length of the field value.
   * Derived from validation rules.
   * @type {number|null}
   * @readonly
   */
  get minLength() {
    return this.rules?.length?.min ?? null;
  }

  /**
   * Validates the field value.
   * @param {string} name - The name of the field.
   * @param {any} value - The value of the field.
   * @param {Object} data - Additional data for validation.
   * @param {Object} context - Additional validation handlers.
   * @param {Function} context.preventSubmit - Prevents the current submission.
   * @returns {Promise<void>}
   */
  async validate(name, value, data, { preventSubmit }) {
    if (this.disabled) {
      return;
    }

    await this.customValidate?.(name, value, {
      data,
      type: this.type,
      addError: this.addError,
      preventSubmit,
    });

    const validator = new Validator(value, this.rules);
    const validationErrors = await validator.validate(this.type);
    validationErrors.forEach((message) => {
      let title = this.title;
      if (this.args.collectionIndex !== undefined) {
        title += ` #${this.args.collectionIndex + 1}`;
      }

      this.addError(name, { title, message });
    });
  }

  <template>{{yield this}}</template>
}
